import React, { useState, useEffect } from 'react';
import { X, Mail, Calendar, Activity, CheckCircle, Ban } from 'lucide-react';
import api from '../../api';
import './UserInspectDrawer.css';

const UserInspectDrawer = ({ user, onClose, onStatusChange }) => {
  const [loading, setLoading] = useState(true);
  const [userDetails, setUserDetails] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (user && user.id) {
      fetchUserDetails();
    }
  }, [user]);

  const fetchUserDetails = async () => {
    setLoading(true);
    try {
      // Gracefully fall back to passed props if API endpoint doesn't exist
      const res = await api.get(`/admin/users/${user.id}/details`).catch(() => null);
      if (res && res.data) {
        setUserDetails(res.data);
      } else {
        setUserDetails(user); // fallback
      }
    } catch (err) {
      setUserDetails(user);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async () => {
    setActionLoading(true);
    try {
      await api.put(`/accounts/user/${user.id}/toggle-status`, { user_id: user.id });
      if (onStatusChange) onStatusChange();
      fetchUserDetails();
    } catch (err) {
      console.error(err);
      alert("Failed to toggle status");
    } finally {
      setActionLoading(false);
    }
  };



  if (!user) return null;

  const data = userDetails || user;

  return (
    <>
      <div className="inspect-drawer-overlay" onClick={onClose} />
      <div className="inspect-drawer">
        <div className="inspect-drawer-header">
          <h2 className="inspect-drawer-title">Inspect User</h2>
          <button className="inspect-close-btn" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="inspect-drawer-content">
          {loading ? (
            <div style={{ textAlign: 'center', marginTop: '40px', color: 'var(--admin-text-muted)' }}>Loading details...</div>
          ) : (
            <>
              <div className="inspect-profile-header">
                <div className="inspect-avatar">
                  {(data.name || data.email || 'U')[0].toUpperCase()}
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.4rem', color: 'var(--admin-text-main)' }}>{data.name || 'Unnamed User'}</h3>
                  <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                    <span className={`admin-badge admin-badge-${data.role === 'university' ? 'blue' : data.role === 'pop' ? 'purple' : 'green'}`}>
                      {data.role === 'pop' ? 'Professor of Practice' : (data.role || 'USER').toUpperCase()}
                    </span>
                    <span style={{
                      color: data.is_active ? '#06d6a0' : '#ffd166',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      background: data.is_active ? 'rgba(6,214,160,0.1)' : 'rgba(255,209,102,0.1)'
                    }}>
                      {data.is_active ? 'ACTIVE' : 'PENDING'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="inspect-info-group">
                <div className="inspect-info-label"><Mail size={14} style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }}/> Email Address</div>
                <div className="inspect-info-value">{data.email}</div>
              </div>

              <div className="inspect-info-group">
                <div className="inspect-info-label"><Calendar size={14} style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }}/> Joined Date</div>
                <div className="inspect-info-value">
                  {data.created_at ? new Date(data.created_at).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : 'Recent / Unknown'}
                </div>
              </div>

              <div className="inspect-info-group">
                <div className="inspect-info-label"><Activity size={14} style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }}/> System ID</div>
                <div className="inspect-info-value" style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{data.id || 'N/A'}</div>
              </div>
            </>
          )}
        </div>

        <div className="inspect-drawer-footer">
          <button 
            className="pop-btn-primary" 
            style={{ flex: 1, background: data.is_active ? 'var(--danger)' : '#06d6a0', borderColor: data.is_active ? 'var(--danger)' : '#06d6a0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
            onClick={handleToggleStatus}
            disabled={actionLoading}
          >
            {data.is_active ? <><Ban size={16} /> Suspend User</> : <><CheckCircle size={16} /> Approve User</>}
          </button>
        </div>
      </div>
    </>
  );
};

export default UserInspectDrawer;
